import { Box, Flex, useToast } from "@chakra-ui/react";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  useCreateAiInterviewerMutation,
  useGenerateGuideFromDocumentsMutation,
  useSaveJobDescriptionFromDocumentsMutation,
} from "../../../graphql";
import { useIsScreenPositionRequired } from "../../../hooks/useIsScreenPositionRequired";
import { CreateLoading } from "../../AiInterviewerCreate/CreateLoading";
import { AiInterviewerWizardContext } from "../AiInterviewerWizardContext";
import { AiInterviewerSetupSubStepFlex } from "../FlexIntake/AiInterviewerSetupSubStepFlex";
import {
  type FlexIntakeFormValues,
  FLEX_INTAKE_DEFAULT_VALUES,
} from "../FlexIntake/types";
import { AgenticChatView } from "./AgenticChatView";
import { DropZone } from "./DropZone";
import { type MaterialItem, MaterialTracker } from "./MaterialTracker";
import type { DocumentState } from "./types";
import { documentsProvidedCount } from "./types";
import type { DocumentUploadState } from "./useDocumentUpload";

/**
 * Agentic Intake create view. Step 1 is identical to Flex Intake setup.
 * Step 2 is the streaming chat experience with tool-call widgets.
 *
 * On transition to step 2 we eagerly create a DRAFT AI Interviewer so
 * that document uploads have an entity to attach to immediately.
 *
 * Demo shortcut: add `?demo=1` (or `&demo=1`) to skip setup and jump
 * straight to the chat with a pre-filled name/title. You can also set
 * `?demo=1&title=My+Role` to customize the prefilled job title.
 */
const AgenticIntakeCreateView: React.FC = () => {
  const {
    setWizardOn,
    setSubStepDisplay,
    setAgenticStepIndex,
    setStepperJobTitle,
  } = useContext(AiInterviewerWizardContext);

  const [searchParams] = useSearchParams();
  const isDemoMode = searchParams.get("demo") === "1";
  const demoTitle =
    searchParams.get("title") || "Senior Customer Success Manager";

  const [subStep, setSubStep] = useState<0 | 1>(isDemoMode ? 1 : 0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSavingJd, setIsSavingJd] = useState(false);
  const [aiInterviewerId, setAiInterviewerId] = useState<string | null>(null);
  const isPositionRequired = useIsScreenPositionRequired();
  const navigate = useNavigate();
  const toast = useToast();

  const [documentState, setDocumentState] = useState<DocumentState>({
    currentInterviewProcess: false,
    idealCandidateProfile: false,
    cultureTeamContext: false,
    pastInterviewTranscripts: false,
  });
  const [jobDescriptionProvided, setJobDescriptionProvided] = useState(false);
  const [completedUploads, setCompletedUploads] = useState<
    DocumentUploadState[]
  >([]);
  const removeUploadRef = useRef<((uploadId: string) => void) | null>(null);
  const sendFileMessageRef = useRef<((file: File) => void) | null>(null);

  const handleStateChange = useCallback(
    (state: {
      documentState: DocumentState;
      jobDescriptionProvided: boolean;
      uploads: DocumentUploadState[];
      removeUpload: (uploadId: string) => void;
      sendFileMessage: (file: File) => void;
    }) => {
      setDocumentState(state.documentState);
      setJobDescriptionProvided(state.jobDescriptionProvided);
      setCompletedUploads(state.uploads.filter((u) => u.phase === "complete"));
      removeUploadRef.current = state.removeUpload;
      sendFileMessageRef.current = state.sendFileMessage;
    },
    []
  );

  const contextAreasProvided = useMemo(() => {
    const docCount = documentsProvidedCount(documentState);
    return docCount + (jobDescriptionProvided ? 1 : 0);
  }, [documentState, jobDescriptionProvided]);

  const handleFilesDropped = useCallback((files: File[]) => {
    files.forEach((file) => sendFileMessageRef.current?.(file));
  }, []);

  const handleRemoveMaterial = useCallback((materialId: string) => {
    removeUploadRef.current?.(materialId);
  }, []);

  const trackerMaterials: MaterialItem[] = useMemo(
    () =>
      completedUploads.map((upload) => ({
        id: upload.id,
        fileName: upload.fileName,
        category: upload.analysis?.category ?? "NONE_OF_THE_ABOVE",
      })),
    [completedUploads]
  );

  const [generateGuide] = useGenerateGuideFromDocumentsMutation();
  const [saveJobDescription] = useSaveJobDescriptionFromDocumentsMutation();

  const handleGenerate = useCallback(() => {
    if (!aiInterviewerId) return;
    setIsGenerating(true);
    generateGuide({ variables: { aiInterviewerId } })
      .then((result) => {
        const id =
          result.data?.generateGuideFromDocuments?.aiInterviewer?.id ??
          aiInterviewerId;
        navigate(`/ai-interviewer/${id}/guide`);
      })
      .catch(() => {
        setIsGenerating(false);
        toast({
          title: "Failed to generate interview guide",
          description: "Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      });
  }, [aiInterviewerId, generateGuide, navigate, toast]);

  const handleSkipGenerate = useCallback(() => {
    if (!aiInterviewerId || isSavingJd) return;
    setIsSavingJd(true);
    saveJobDescription({ variables: { aiInterviewerId } })
      .then(() => {
        navigate(`/ai-interviewer/${aiInterviewerId}/guide`);
      })
      .catch(() => {
        setIsSavingJd(false);
        toast({
          title: "Failed to save job description",
          description: "Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      });
  }, [aiInterviewerId, isSavingJd, saveJobDescription, navigate, toast]);

  const [createAiInterviewer] = useCreateAiInterviewerMutation({
    onCompleted: (data) => {
      const newId = data?.createAiInterviewer?.aiInterviewer?.id;
      if (newId) {
        setAiInterviewerId(newId);
      }
    },
    onError: () => {
      toast({
        title: "Error setting up AI interviewer",
        description: "Please go back and try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    },
  });

  useEffect(() => {
    setWizardOn?.(true);
    if (isDemoMode) {
      createAiInterviewer({
        variables: {
          name: "Demo AI Interviewer",
          displayName: demoTitle,
          jobDescription: "",
          autoCreateGuide: false,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const methods = useForm<FlexIntakeFormValues>({
    defaultValues: isDemoMode
      ? {
          ...FLEX_INTAKE_DEFAULT_VALUES,
          name: "Demo AI Interviewer",
          displayName: demoTitle,
          autoCreateGuide: true,
        }
      : FLEX_INTAKE_DEFAULT_VALUES,
    mode: "onTouched",
  });

  const displayName = methods.watch("displayName") || "";
  const autoCreateGuide = methods.watch("autoCreateGuide");

  const validateAndAdvance = useCallback(async () => {
    const fieldsToValidate: (keyof FlexIntakeFormValues)[] = [
      "name",
      "displayName",
    ];
    if (isPositionRequired) {
      fieldsToValidate.push("positionId");
    }
    const valid = await methods.trigger(fieldsToValidate);
    if (valid) {
      const values = methods.getValues();
      try {
        await createAiInterviewer({
          variables: {
            name: values.name,
            displayName: values.displayName,
            jobDescription: "",
            autoCreateGuide: false,
            positionId: values.positionId || null,
            staticLinkEnabled: values.staticLinkEnabled,
          },
        });
        setSubStep(1);
      } catch {
        // onError handler on the mutation already shows a toast
      }
    }
  }, [methods, isPositionRequired, createAiInterviewer]);

  const subStepDisplayValue = useMemo(
    () =>
      subStep === 0
        ? {
            header: "Set up AI interviewer",
            description: "Configure your AI interviewer and interview guide.",
            nextLabel: "Next: Job description",
            onNext: validateAndAdvance,
          }
        : {
            header: "Provide materials for your guide",
            description:
              "Use BrightHire AI to add your job description and optional materials.",
            nextLabel: undefined,
            onNext: undefined,
            onBack: () => setSubStep(0),
            stepperTitle: displayName || undefined,
          },
    [subStep, validateAndAdvance, displayName]
  );

  useEffect(() => {
    setSubStepDisplay?.(subStepDisplayValue);
    return () => setSubStepDisplay?.(null);
  }, [subStepDisplayValue, setSubStepDisplay]);

  // Drive the agentic stepper visual state
  useEffect(() => {
    if (isGenerating) {
      setAgenticStepIndex?.(2, 0); // Guide: Generating
    } else if (subStep === 1) {
      setAgenticStepIndex?.(1, 0); // Training
    } else {
      setAgenticStepIndex?.(0, 0); // Start
    }
  }, [subStep, isGenerating, setAgenticStepIndex]);

  useEffect(() => {
    if (displayName) {
      setStepperJobTitle?.(displayName);
    }
  }, [displayName, setStepperJobTitle]);

  if (isGenerating) {
    return (
      <Box w="100%" overflow="auto">
        <Flex
          direction="column"
          align="center"
          maxWidth="692px"
          p="12"
          mb="8"
          mx="auto"
          borderRadius="xl"
          boxShadow="base"
          border="1px solid"
          borderColor="blue.200"
          bg="white"
        >
          <CreateLoading autoCreateGuide />
        </Flex>
      </Box>
    );
  }

  if (subStep === 0) {
    return (
      <AiInterviewerSetupSubStepFlex
        methods={methods}
        onNext={validateAndAdvance}
        onCancel={() => navigate("/ai-interviewers")}
      />
    );
  }

  return (
    <Flex w="100%" h="100%" justify="center" pb={6}>
      <DropZone onFilesDropped={handleFilesDropped} borderRadius="16px">
        <Flex
          w="100%"
          maxW="1116px"
          h="100%"
          borderRadius="16px"
          overflow="hidden"
          border="1px solid"
          borderColor="rgba(0, 116, 221, 0.60)"
          bg="white"
          mx="auto"
        >
          <Box flex="1" minW="0" h="100%">
            <AgenticChatView
              displayName={displayName}
              aiInterviewerId={aiInterviewerId}
              autoCreateGuide={autoCreateGuide}
              onGenerate={handleGenerate}
              onSkipGenerate={handleSkipGenerate}
              onStateChange={handleStateChange}
            />
          </Box>
          <Box w="376px" flexShrink={0} h="100%">
            <MaterialTracker
              contextAreasProvided={contextAreasProvided}
              jobDescriptionProvided={jobDescriptionProvided}
              documentState={documentState}
              materials={trackerMaterials}
              onRemoveMaterial={handleRemoveMaterial}
            />
          </Box>
        </Flex>
      </DropZone>
    </Flex>
  );
};

export { AgenticIntakeCreateView };
